import { createFileRoute } from "@tanstack/react-router"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import HTMLFlipBook from "react-pageflip"
import { neonHeadingClass } from "@/components/ui/neon"
import { PageVignette } from "@/components/ui/page-vignette"
import { SectionTitle } from "@/components/ui/section-title"
import { SharedButton } from "@/components/ui/shared-button"
import { StarField } from "@/components/ui/star-field"
import { ISSUES, type Issue } from "@/data/comics"
import { cn } from "@/lib/utils"

export const Route = createFileRoute("/comics")({
  head: () => ({
    meta: [
      { title: "Comics | EKLO-Verse" },
      {
        name: "description",
        content:
          "Read the EKLO comic saga — Betrayal Among the Stars, Lockdown: Traitor, and Descent: No Return. The trilogy that opens the EKLO-Verse.",
      },
      { property: "og:title", content: "Comics | EKLO-Verse" },
      {
        property: "og:description",
        content: "The EKLO comic trilogy — origin, lockdown, descent.",
      },
      {
        property: "og:image",
        content:
          "https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZmjxrU8LDcnsNIHCbjWw0tk8QoXJ4EY1ZFMKy",
      },
      {
        name: "twitter:image",
        content:
          "https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZmjxrU8LDcnsNIHCbjWw0tk8QoXJ4EY1ZFMKy",
      },
      { property: "og:url", content: "https://eklo-seven.vercel.app/comics" },
    ],
    links: [{ rel: "canonical", href: "https://eklo-seven.vercel.app/comics" }],
  }),
  component: ComicsPage,
})

const META_LABEL_CLS =
  "font-mono font-semibold text-[10px] text-muted-foreground uppercase tracking-[0.18em]"
const META_VALUE_CLS =
  "mt-1.5 font-medium font-mono text-sm text-foreground uppercase tracking-[0.04em]"
const FRAME_HEAD_CLS =
  "flex items-center justify-between gap-4 border-white/15 border-b font-mono font-semibold uppercase tracking-[0.22em]"

const pad2 = (n: number) => String(n).padStart(2, "0")

function splitTitle(title: string): Array<string> {
  const words = title.split(" ")
  if (words.length <= 1) return [title]
  const mid = Math.floor(words.length / 2)
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")]
}

function ComicsPage() {
  const [lightboxIssue, setLightboxIssue] = useState<Issue | null>(null)

  const open = useCallback((issue: Issue) => setLightboxIssue(issue), [])
  const close = useCallback(() => setLightboxIssue(null), [])

  useEffect(() => {
    if (!lightboxIssue) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    window.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [lightboxIssue, close])

  return (
    <div className="fade-in relative animate-in overflow-x-hidden px-4 pt-36 pb-24 duration-500 sm:px-8 sm:pt-30 sm:pb-30">
      <StarField />
      <PageVignette />

      <div className="relative z-10 mx-auto max-w-350">
        <div className="mb-20 sm:mb-32">
          <SectionTitle
            prefix="The bridge"
            accent="between mediums."
            className="mb-5 justify-center sm:mb-6"
          />
          <p className="mx-auto max-w-[62ch] text-center font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.7]">
            Three issues drawn parallel to the trilogy. Drag a page corner or
            click the side to turn. Open any book to read full-screen.
          </p>
        </div>

        <div className="flex flex-col gap-20 sm:gap-32 lg:gap-40">
          {ISSUES.map((issue) => (
            <IssueRow key={issue.num} issue={issue} onOpen={open} />
          ))}
        </div>
      </div>

      {lightboxIssue && <Lightbox issue={lightboxIssue} onClose={close} />}
    </div>
  )
}

const IssueRow = memo(function IssueRow({
  issue,
  onOpen,
}: {
  issue: Issue
  onOpen: (issue: Issue) => void
}) {
  const titleLines = useMemo(() => splitTitle(issue.title), [issue.title])
  const handleOpen = useCallback(() => onOpen(issue), [issue, onOpen])

  return (
    <div className="relative flex flex-col gap-8 sm:gap-10 lg:block lg:min-h-145">
      <img
        src={issue.cover}
        alt={`${issue.title} — cover`}
        width={1}
        height={1}
        loading="lazy"
        decoding="async"
        className="sr-only"
        aria-hidden="true"
      />
      <div className="relative z-1 flex max-w-full flex-col items-center gap-3 text-center sm:gap-3.5 lg:items-start lg:pr-105 lg:text-left">
        <span className={META_LABEL_CLS}>Issue · {pad2(issue.num)}</span>
        <h2
          className={cn(
            neonHeadingClass,
            "mt-2 mb-1 text-[clamp(1.4rem,3vw,2.25rem)] leading-[1] tracking-[-0.02em]"
          )}
        >
          {titleLines.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </h2>
        {issue.subtitle && (
          <span className="font-mono font-semibold text-[11px] text-muted-foreground uppercase tracking-[0.22em]">
            {issue.subtitle}
          </span>
        )}
        <p className="mt-3 max-w-[42ch] font-mono text-base text-foreground leading-relaxed">
          {issue.tagline}
        </p>
        <p className="mt-2 max-w-[48ch] font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.7]">
          {issue.body}
        </p>
        <span aria-hidden="true" className="mt-5 block h-px w-56 bg-white/20" />
        <div className="mt-4 flex gap-12">
          <Meta label="Pages" value={String(issue.pages.length)} />
          <Meta label="Format" value="Digital · turn-to-read" />
        </div>
        <div className="mt-6 hidden lg:block">
          <SharedButton onClick={handleOpen}>
            Open full-screen reader
          </SharedButton>
        </div>
      </div>
      <div className="filter-[drop-shadow(-30px_30px_60px_rgba(0,0,0,0.6))] relative z-5 flex items-center justify-center lg:absolute lg:top-1/2 lg:right-0 lg:-translate-y-1/2 lg:justify-end">
        <div className="flex w-full max-w-180 flex-col border border-white/15 bg-[#07101f]/55">
          <div
            className={cn(
              FRAME_HEAD_CLS,
              "px-3 py-2 text-[10px] text-muted-foreground"
            )}
          >
            <span>EKLO-VERSE — VOL. {pad2(issue.num)}</span>
            <span>{issue.pages.length} PAGES</span>
          </div>
          <div className="flex justify-center px-3">
            <IssueFlipBook issue={issue} />
          </div>
        </div>
      </div>
      <div className="flex justify-center lg:hidden">
        <SharedButton onClick={handleOpen}>
          Open full-screen reader
        </SharedButton>
      </div>
    </div>
  )
})

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className={META_LABEL_CLS}>{label}</span>
      <div className={META_VALUE_CLS}>{value}</div>
    </div>
  )
}

type BookSize = { w: number; h: number }

function computeBookSize(large: boolean, vw: number, vh: number): BookSize {
  if (large) {
    const maxByWidth = Math.floor((vw - 48) / 2)
    const maxByHeight = Math.floor((vh - 120) / 1.5)
    const w = Math.max(180, Math.min(540, maxByWidth, maxByHeight))
    return { w, h: Math.round(w * 1.5) }
  }
  const maxPageW = Math.floor((vw - 64) / 2)
  if (vw < 480) {
    const w = Math.max(180, Math.min(220, maxPageW))
    return { w, h: Math.round(w * 1.5) }
  }
  if (vw < 720) {
    const w = Math.min(280, maxPageW)
    return { w, h: Math.round(w * 1.5) }
  }
  return { w: 360, h: 540 }
}

function IssueFlipBook({
  issue,
  large = false,
}: {
  issue: Issue
  large?: boolean
}) {
  const [mounted, setMounted] = useState(false)
  const [size, setSize] = useState<BookSize>(() =>
    large ? { w: 480, h: 720 } : { w: 360, h: 540 }
  )
  // biome-ignore lint/suspicious/noExplicitAny: react-pageflip ref type not exported cleanly
  const bookRef = useRef<any>(null)
  const peekedRef = useRef(false)

  useEffect(() => {
    setMounted(true)
    const update = () =>
      setSize(computeBookSize(large, window.innerWidth, window.innerHeight))
    update()
    window.addEventListener("resize", update)
    return () => window.removeEventListener("resize", update)
  }, [large])

  useEffect(() => {
    if (!large) return
    const onKey = (e: KeyboardEvent) => {
      const flip = bookRef.current?.pageFlip?.()
      if (!flip) return
      if (e.key === "ArrowRight") flip.flipNext()
      else if (e.key === "ArrowLeft") flip.flipPrev()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [large])

  const allPages = useMemo(
    () => [issue.cover, ...issue.pages],
    [issue.cover, issue.pages]
  )

  const onEnter = useCallback(() => {
    if (large) return
    const flip = bookRef.current?.pageFlip?.()
    if (!flip) return
    if (flip.getCurrentPageIndex?.() === 0) {
      peekedRef.current = true
      flip.flipNext()
    }
  }, [large])

  const onLeave = useCallback(() => {
    if (large || !peekedRef.current) return
    const flip = bookRef.current?.pageFlip?.()
    if (!flip) return
    if (flip.getCurrentPageIndex?.() <= 1) flip.flipPrev()
    peekedRef.current = false
  }, [large])

  const { w, h } = size

  if (!mounted) {
    return (
      <div
        className="border border-white/15 bg-[#0c1a2e]"
        style={{ width: w * 2, height: h }}
      />
    )
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentational hover wrapper
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <HTMLFlipBook
        ref={bookRef}
        width={w}
        height={h}
        size="fixed"
        minWidth={300}
        maxWidth={700}
        minHeight={450}
        maxHeight={1000}
        drawShadow
        flippingTime={800}
        usePortrait={false}
        startZIndex={0}
        autoSize={false}
        maxShadowOpacity={0.5}
        showCover
        mobileScrollSupport={false}
        useMouseEvents
        swipeDistance={30}
        showPageCorners
        disableFlipByClick={false}
        className=""
        style={{}}
        startPage={0}
        clickEventForward={false}
      >
        {allPages.map((src, i) => (
          <div
            key={src}
            className="h-full w-full select-none overflow-hidden bg-white"
          >
            <img
              src={src}
              alt={`Issue ${issue.num} page ${i}`}
              width={1200}
              height={1800}
              draggable={false}
              className="pointer-events-none block h-full w-full object-cover"
            />
          </div>
        ))}
      </HTMLFlipBook>
    </div>
  )
}

function Lightbox({ issue, onClose }: { issue: Issue; onClose: () => void }) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click closes modal
    <div
      className="fade-in fixed inset-0 z-10000 flex animate-in items-center justify-center bg-[#02040c]/95 px-4 py-3 backdrop-blur-md sm:px-6"
      onClick={onClose}
      onKeyUp={(e) => {
        if (e.key === "Escape") onClose()
      }}
    >
      <div
        className="relative flex max-h-[96vh] w-fit max-w-[96vw] flex-col border border-white/20 bg-[#07101f]/60"
        onClick={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Issue ${issue.num} reader`}
      >
        <div
          className={cn(
            FRAME_HEAD_CLS,
            "px-4 py-2 text-[11px] text-foreground"
          )}
        >
          <span className="truncate">
            EKLO-VERSE — VOL. {pad2(issue.num)} — {issue.title}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close reader"
          className="absolute -top-2 -right-2 z-10 flex size-9 items-center justify-center bg-brand text-lg text-white leading-none transition-colors hover:bg-brand/85 sm:-top-4 sm:-right-4"
        >
          ×
        </button>
        <div className="flex flex-1 items-center justify-center px-4">
          <IssueFlipBook issue={issue} large />
        </div>
        <div className="hidden border-white/15 border-t px-4 py-1.5 text-center font-mono font-semibold text-[9px] text-muted-foreground uppercase tracking-[0.2em] lg:block">
          ← → keys · click corner to turn · ESC to close
        </div>
      </div>
    </div>
  )
}
